import { useEffect, useMemo, useRef, useState } from "react";

import { get } from "@/utils/get";

import { Autocomplete, Box, TextField } from "@mui/material";
import PropTypes from "prop-types";

import PlayControl from "./PlayControl";

function InputMedia({
  fullWidth = true,
  getOptionLabel,
  hasEmpty,
  selects,
  selectValue = "value",
  value,
  error,
  getSourceDependency,
  getSourceGeneration,
  loop = false,
  onChange,
  id,
  name,
  label,
  helperText,
  required,
  ...props
}) {
  const options = Array.isArray(selects) ? selects : [];

  const optionValue = (option) => (typeof option === "object" && option !== null ? option[selectValue] : option);

  // Player
  const audioRef = useRef({});

  const [activePlayer, setActivePlayer] = useState({});

  useEffect(() => {
    const audioPlayer = audioRef.current;

    return () => {
      if (audioPlayer) {
        audioPlayer.removeEventListener("timeupdate", null);
        audioPlayer.removeEventListener("loadedmetadata", null);
      }
    };
  }, []);

  const src = useMemo(() => {
    if (getSourceDependency(activePlayer)) {
      return getSourceGeneration(activePlayer);
    }

    return null;
  }, [activePlayer, getSourceDependency, getSourceGeneration]);

  return (
    <>
      <Autocomplete
        {...props}
        id={id}
        fullWidth={fullWidth}
        options={options}
        disableClearable={hasEmpty === undefined || hasEmpty === false}
        value={options.find((option) => optionValue(option) === value) ?? null}
        onChange={(e, newValue) => onChange(id, newValue !== null ? optionValue(newValue) : hasEmpty)}
        onClose={() => setActivePlayer({})}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, val) => optionValue(option) === optionValue(val)}
        renderOption={({ key, ...optionProps }, option) => (
          <Box component="li" key={key} {...optionProps}>
            {option?.token && (
              <PlayControl
                audio={option}
                activePlayer={activePlayer}
                setActivePlayer={setActivePlayer}
                audioRef={audioRef}
              />
            )}
            {getOptionLabel(option)}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            placeholder={name ?? label}
            error={!!get(name ?? id, error)}
            helperText={get(name ?? id, error)?.[0] || helperText}
          />
        )}
      />
      <audio key={activePlayer.id} ref={audioRef} id={`audio-${activePlayer.id}`} loop={loop}>
        <source src={src} />
      </audio>
    </>
  );
}

InputMedia.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  error: PropTypes.object,
  fullWidth: PropTypes.bool,
  helperText: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hasEmpty: PropTypes.any,
  selects: PropTypes.array,
  getOptionLabel: PropTypes.func,
  selectValue: PropTypes.string,
  getSourceDependency: PropTypes.func,
  getSourceGeneration: PropTypes.func,
  loop: PropTypes.bool,
};

export default InputMedia;
